import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { ExternalLink, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Maximize2, Loader2 } from "lucide-react";
import { useWidgetProps } from "../use-widget-props";
import { useTheme } from "../use-theme";
import { useDisplayMode } from "../use-display-mode";
import { useWidgetState } from "../use-widget-state";

type Product = {
  id: string;
  name: string;
  brand: string;
  price: number;
  discountedPrice?: number | null;
  currency: string;
  image: string;
  url: string;
  retailer: string;
  score?: number;
};

type ToolOutput = {
  summary: string;
  searchedProduct: Product;
  exactMatches?: Product[];
  alternatives: Product[];
  totalAlternatives: number;
  _streaming?: boolean;
};

type SortOption = "relevance" | "price-low" | "price-high";

function formatBrand(brand: string): string {
  return brand
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPrice(price: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(price);
}

function ProductCard({ product, isDark, onClick }: { product: Product; isDark: boolean; onClick: () => void }) {
  const hasDiscount = product.discountedPrice != null && product.discountedPrice < product.price;

  return (
    <article
      onClick={onClick}
      className="group cursor-pointer flex flex-col overflow-hidden transition-all"
    >
      <div className={`relative aspect-square overflow-hidden rounded-2xl border ${
        isDark ? "border-white/10 bg-gray-800" : "border-black/5 bg-gray-100"
      }`}>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop";
          }}
        />
        {hasDiscount && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-md">
            {Math.round((1 - product.discountedPrice! / product.price) * 100)}% OFF
          </div>
        )}
      </div>

      <div className="flex-1 pt-3 flex flex-col gap-1">
        {product.retailer && (
          <p className={`text-sm tracking-[-0.3px] ${isDark ? "text-[#8f8f8f]" : "text-[#8f8f8f]"}`}>
            {formatBrand(product.retailer).toUpperCase()}
          </p>
        )}
        <h3 className={`font-medium text-[17px] leading-tight tracking-[-0.43px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
          {formatBrand(product.brand)}
        </h3>
        <p className={`text-sm tracking-[-0.3px] line-clamp-2 ${isDark ? "text-[#8f8f8f]" : "text-[#5d5d5d]"}`}>
          {product.name}
        </p>
        {hasDiscount ? (
          <div className="flex items-center gap-2">
            <p className={`text-sm tracking-[-0.3px] font-medium text-red-500`}>
              {formatPrice(product.discountedPrice!, product.currency)}
            </p>
            <p className={`text-xs tracking-[-0.3px] line-through ${isDark ? "text-[#8f8f8f]" : "text-[#8f8f8f]"}`}>
              {formatPrice(product.price, product.currency)}
            </p>
          </div>
        ) : (
          <p className={`text-sm tracking-[-0.3px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
            {formatPrice(product.price, product.currency)}
          </p>
        )}
      </div>
    </article>
  );
}

function SkeletonCard({ isDark }: { isDark: boolean }) {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      <div className={`aspect-square rounded-2xl ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
      <div className="flex flex-col gap-2 pt-1">
        <div className={`h-3 w-16 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
        <div className={`h-4 w-24 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
        <div className={`h-3 w-32 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
        <div className={`h-3 w-14 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
      </div>
    </div>
  );
}

function LoadingState({ isDark }: { isDark: boolean }) {
  return (
    <div className={`w-full p-4 ${isDark ? "bg-[#212121] rounded-2xl" : "bg-white"}`}>
      {/* Skeleton header */}
      <div className="pb-4">
        <div className={`h-6 w-32 rounded mb-4 animate-pulse ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
        <div className="flex gap-4">
          <div className={`flex-shrink-0 w-[100px] h-[84px] rounded-xl animate-pulse ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
          <div className="flex-1 flex flex-col gap-2 animate-pulse">
            <div className={`h-3 w-20 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
            <div className={`h-4 w-28 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
            <div className={`h-3 w-40 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
            <div className={`h-3 w-16 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
          </div>
        </div>
      </div>
      {/* Skeleton section title */}
      <div className={`h-6 w-28 rounded mb-4 animate-pulse ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
      {/* Skeleton cards */}
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => <SkeletonCard key={i} isDark={isDark} />)}
      </div>
    </div>
  );
}

export default function App() {
  const props = useWidgetProps<ToolOutput>();
  const theme = useTheme() ?? "light";
  const displayMode = useDisplayMode() ?? "inline";
  const isDark = theme === "dark";
  const isFullscreen = displayMode === "fullscreen";

  const [, setWidgetState] = useWidgetState({ selectedId: null as string | null });
  const [sortBy, setSortBy] = useState<SortOption>("relevance");
  const [showAllAlternatives, setShowAllAlternatives] = useState(false);

  // Responsive: detect narrow containers (mobile / iOS ChatGPT)
  const containerRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width < 400);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const exactMatches = props?.exactMatches ?? [];
  const exactScrollRef = useRef<HTMLDivElement>(null);
  const scrollExact = useCallback((dir: "left" | "right") => {
    const el = exactScrollRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8;
    el.scrollBy({ left: dir === "left" ? -scrollAmount : scrollAmount, behavior: "smooth" });
  }, []);

  const sortedSimilarFinds = useMemo(() => {
    if (!props?.alternatives) return [];
    const items = [...props.alternatives];
    switch (sortBy) {
      case "price-low":
        return items.sort((a, b) => a.price - b.price);
      case "price-high":
        return items.sort((a, b) => b.price - a.price);
      case "relevance":
      default:
        return items.sort((a, b) => (b.score || 0) - (a.score || 0));
    }
  }, [props?.alternatives, sortBy]);

  if (!props || !props.searchedProduct) {
    return <LoadingState isDark={isDark} />;
  }

  const cols = isFullscreen ? 4 : isNarrow ? 2 : 3;
  const displayedSimilarFinds = showAllAlternatives
    ? sortedSimilarFinds
    : sortedSimilarFinds.slice(0, isFullscreen ? 12 : 8);

  const hasMoreSimilarFinds = sortedSimilarFinds.length > displayedSimilarFinds.length;

  const handleOpenProduct = (id: string, url: string) => {
    setWidgetState({ selectedId: id });
    if (window.openai?.openExternal) {
      window.openai?.openExternal({ href: url });
    } else {
      window.open(url, "_blank");
    }
  };

  const handleRequestFullscreen = async () => {
    if (window.openai?.requestDisplayMode) {
      await window.openai?.requestDisplayMode({ mode: "fullscreen" });
    } else if (window.webplus?.requestDisplayMode) {
      await window.webplus?.requestDisplayMode({ mode: "fullscreen" });
    }
  };

  const effectivePrice = (p: Product) => p.discountedPrice ?? p.price;
  const allProducts = [...exactMatches, ...props.alternatives];
  const cheaperCount = allProducts.filter(
    (alt) => effectivePrice(alt) < props.searchedProduct.price
  ).length;

  return (
    <div ref={containerRef} className={`w-full overflow-hidden ${isDark ? "bg-[#212121] rounded-2xl" : "bg-white"}`}>
      <div className="p-4">
        <span className="sr-only">{props.summary}</span>
        {/* Header with original product */}
        <div className="pb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-[22px] font-semibold tracking-[-0.43px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
              Original Piece
            </h2>
            <div className="flex gap-1">
              {!isFullscreen && (
                <button
                  onClick={handleRequestFullscreen}
                  className={`p-2 rounded-full transition-colors ${
                    isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-[#5d5d5d]"
                  }`}
                  aria-label="Enter fullscreen"
                >
                  <Maximize2 className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => handleOpenProduct(props.searchedProduct.id, props.searchedProduct.url)}
                className={`p-2 rounded-full transition-colors ${
                  isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-[#5d5d5d]"
                }`}
                aria-label="View original product"
              >
                <ExternalLink className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex gap-4">
            <div className={`flex-shrink-0 w-[100px] h-[84px] rounded-xl overflow-hidden border ${
              isDark ? "border-white/10 bg-gray-800" : "border-black/5 bg-[#f8f7f5]"
            }`}>
              <img
                src={props.searchedProduct.image}
                alt={props.searchedProduct.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop";
                }}
              />
            </div>

            <div className="flex-1 min-w-0">
              {props.searchedProduct.retailer && (
                <p className={`text-sm tracking-[-0.43px] ${isDark ? "text-[#8f8f8f]" : "text-[#5d5d5d]"}`}>
                  {formatBrand(props.searchedProduct.retailer).toUpperCase()}
                </p>
              )}
              <h3 className={`font-semibold text-[17px] tracking-[-0.43px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
                {formatBrand(props.searchedProduct.brand)}
              </h3>
              <p className={`text-[17px] font-medium tracking-[-0.43px] truncate ${isDark ? "text-[#8f8f8f]" : "text-[#5d5d5d]"}`}>
                {props.searchedProduct.name}
              </p>
              <p className={`text-sm tracking-[-0.3px] mt-1 ${isDark ? "text-[#8f8f8f]" : "text-[#5d5d5d]"}`}>
                {formatPrice(props.searchedProduct.price, props.searchedProduct.currency)}
              </p>

              <div className="flex gap-4 mt-2">
                <span className={`text-xs ${isDark ? "text-gray-400" : "text-[#5d5d5d]"}`}>
                  {props.totalAlternatives} similar finds
                </span>
                {cheaperCount > 0 && (
                  <span className="text-xs text-green-600 font-medium">
                    {cheaperCount} cheaper options
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Exact Matches section — horizontal scroll */}
        {exactMatches.length > 0 && (
          <>
            <div className="py-3">
              <h3 className={`text-[22px] font-semibold tracking-[-0.43px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
                Exact Matches
              </h3>
            </div>
            <div className="pb-4 relative group/scroll">
              <div
                ref={exactScrollRef}
                className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {exactMatches.map((alt) => (
                  <div key={alt.id} className={`flex-shrink-0 ${cols === 4 ? "w-[calc(25%-12px)]" : cols === 2 ? "w-[calc(50%-8px)]" : "w-[calc(33.333%-11px)]"}`}>
                    <ProductCard
                      product={alt}
                      isDark={isDark}
                      onClick={() => handleOpenProduct(alt.id, alt.url)}
                    />
                  </div>
                ))}
              </div>
              {exactMatches.length > cols && (
                <>
                  <button
                    onClick={() => scrollExact("left")}
                    className={`absolute left-0 top-1/3 -translate-y-1/2 z-10 w-10 h-10 rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover/scroll:opacity-100 transition-opacity ${
                      isDark ? "bg-gray-800 text-white border border-white/10" : "bg-white text-[#0d0d0d] border border-black/10"
                    }`}
                    aria-label="Scroll left"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => scrollExact("right")}
                    className={`absolute right-0 top-1/3 -translate-y-1/2 z-10 w-10 h-10 rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover/scroll:opacity-100 transition-opacity ${
                      isDark ? "bg-gray-800 text-white border border-white/10" : "bg-white text-[#0d0d0d] border border-black/10"
                    }`}
                    aria-label="Scroll right"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {/* Similar Finds section */}
        <div className="py-3 flex items-center justify-between">
          <h3 className={`text-[22px] font-semibold tracking-[-0.43px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
            Similar Finds
          </h3>
          <div className="flex items-center gap-2">
            <label className={`text-xs ${isDark ? "text-gray-400" : "text-[#5d5d5d]"}`}>Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className={`text-xs rounded-lg px-2 py-1 border ${
                isDark
                  ? "bg-gray-800 border-gray-700 text-white"
                  : "bg-white border-gray-200 text-gray-900"
              }`}
            >
              <option value="relevance">Most Similar</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>
        </div>

        <div className="pt-2 pb-4">
          {displayedSimilarFinds.length > 0 ? (
            <>
              <div className={`grid gap-4 ${cols === 4 ? "grid-cols-4" : cols === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
                {displayedSimilarFinds.map((alt) => (
                  <ProductCard
                    key={alt.id}
                    product={alt}
                    isDark={isDark}
                    onClick={() => handleOpenProduct(alt.id, alt.url)}
                  />
                ))}
              </div>

              {(hasMoreSimilarFinds || showAllAlternatives) && sortedSimilarFinds.length > (isFullscreen ? 12 : 8) && (
                <button
                  onClick={() => setShowAllAlternatives(!showAllAlternatives)}
                  className={`w-full mt-4 py-3 rounded-xl border flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                    isDark
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                      : "border-gray-200 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {showAllAlternatives ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show All {sortedSimilarFinds.length} Similar Finds
                    </>
                  )}
                </button>
              )}
            </>
          ) : props._streaming ? (
            <div className={`grid gap-4 ${cols === 4 ? "grid-cols-4" : cols === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
              {Array.from({ length: cols }).map((_, i) => <SkeletonCard key={i} isDark={isDark} />)}
            </div>
          ) : exactMatches.length === 0 ? (
            <div className={`text-center py-8 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              <p>No alternatives found for this product.</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
